import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import HeroSection from '../../components/events/HeroSection/HeroSection';
import EventCard from '../../components/events/EventCard/EventCard';
import PopularEventCard from '../../components/events/PopularEventCard/PopularEventCard';
import CommunityCard from '../../components/communities/CommunityCard/CommunityCard';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import Button from '../../components/ui/Button/Button';
import { useEvents } from '../../hooks/useEvents';
import { useChannels } from '../../hooks/useChannels';
import { useSavedEvents } from '../../hooks/useSavedEvents';
import { useGeneral } from '../../contexts';
import Services from '../../services/Services';
import transitionStyles from '../../styles/transitions.module.css';
import { EventListItem, ChannelListItem } from '../../types';
import { generateEventUrl, generateCommunityUrl } from '../../utils/urlSlug';
import SEO from '../../components/SEO';
import { generateWebsiteSchema } from '../../utils/seoSchemas';
import styles from './HomePage.module.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token, user } = auth;
  const { getTranslation } = language;
  const isMountedRef = useRef(true);
  const userId = user?.id || '';

  // Saved events state
  const [activeTab, setActiveTab] = useState<'upcoming' | 'saved'>('upcoming');
  // Mobile pagination state for upcoming events
  const [mobileEventsToShow, setMobileEventsToShow] = useState(4);
  // Mobile pagination state for popular communities
  const [mobileCommunitiesToShow, setMobileCommunitiesToShow] = useState(4);
  // Track if we're on mobile
  const [isMobile, setIsMobile] = useState(false);
  
  // Refs for scroll containers
  const upcomingEventsScrollRef = useRef<HTMLDivElement>(null);
  const savedEventsScrollRef = useRef<HTMLDivElement>(null);
  const popularCommunitiesScrollRef = useRef<HTMLDivElement>(null);
  const popularEventsScrollRef = useRef<HTMLDivElement>(null);
  const certificateEventsScrollRef = useRef<HTMLDivElement>(null);
  
  // Scroll state for chevron visibility
  const [upcomingEventsScrollState, setUpcomingEventsScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [savedEventsScrollState, setSavedEventsScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [popularCommunitiesScrollState, setPopularCommunitiesScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [popularEventsScrollState, setPopularEventsScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [certificateEventsScrollState, setCertificateEventsScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const { 
    savedEvents, 
    loading: savedLoading, 
    fetchSavedEvents,
    toggleBookmark,
    isEventSaved
  } = useSavedEvents({
    token: token || '',
    userID: userId,
    autoFetch: false
  });

  useEffect(() => {
    isMountedRef.current = true;
    
    // Check if mobile on mount and resize
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Function to check scroll state
  const checkScrollState = useCallback((container: HTMLDivElement | null, setState: React.Dispatch<React.SetStateAction<{ canScrollLeft: boolean; canScrollRight: boolean }>>) => {
    if (!container || isMobile) {
      setState({ canScrollLeft: false, canScrollRight: false });
      return;
    }
    
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasScrollableContent = scrollWidth > clientWidth;
    const canScrollLeft = hasScrollableContent && scrollLeft > 5; // 5px threshold for better UX
    const canScrollRight = hasScrollableContent && scrollLeft < scrollWidth - clientWidth - 5; // 5px threshold
    
    setState({ canScrollLeft, canScrollRight });
  }, [isMobile]);

  // Function to scroll container by 4 items
  const scrollByItems = useCallback((container: HTMLDivElement | null, direction: 'left' | 'right') => {
    if (!container) return;
    
    const itemWidth = container.querySelector('*')?.getBoundingClientRect().width || 0;
    const gap = 8; // gap between items (padding/4 = 32px/4 = 8px)
    const scrollAmount = (itemWidth + gap) * 4;
    
    const currentScroll = container.scrollLeft;
    const newScroll = direction === 'right' 
      ? currentScroll + scrollAmount 
      : currentScroll - scrollAmount;
    
    container.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  }, []);

  const { events: upcomingEvents, loading: loadingUpcoming } = useEvents({
    token: token || '',
    userID: userId,
    loadingKey: 'home.upcomingEvents'
  });

  const { events: popularEvents, loading: loadingPopular } = useEvents({
    token: token || '',
    loadingKey: 'home.popularEvents'
  });

  const { events: certificateEvents, loading: loadingCertificateEvents } = useEvents({
    token: token || '',
    loadingKey: 'home.certificateEvents',
    useCertificates: true
  });

  const { channels: popularChannels, loading: loadingChannels, refresh: refreshPopularChannels } = useChannels({
    token: token || '',
    popular: true,
    loadingKey: 'home.popularChannels'
  });

  const { channels: joinedChannels, refresh: refreshJoinedChannels } = useChannels({
    token: token || '',
    userID: userId,
    popular: false
  });

  // Local optimistic state for joined channel IDs
  const [localJoinedIds, setLocalJoinedIds] = useState<Set<string>>(new Set());

  // Sync from hook data
  useEffect(() => {
    setLocalJoinedIds(new Set(joinedChannels.map(ch => ch.channelID)));
  }, [joinedChannels]);

  // Create a Set of joined channel IDs for quick lookup
  const joinedChannelIds = localJoinedIds;

  // Create a map of channelID to admin status
  const channelAdminMap = useMemo(() => {
    const map = new Map<string, boolean>();
    joinedChannels.forEach(channel => {
      map.set(channel.channelID, String(channel.isAdmin) === "1");
    });
    return map;
  }, [joinedChannels]);


  // Fetch saved events when tab is switched to saved or when user changes
  useEffect(() => {
    if (activeTab === 'saved' && user?.id && token) {
      fetchSavedEvents();
    }
  }, [activeTab, user?.id, token, fetchSavedEvents]);

  // Reset mobile pagination when tab changes
  useEffect(() => {
    setMobileEventsToShow(4);
  }, [activeTab]);

  // Handle show more button clicks
  const handleShowMore = useCallback(() => {
    setMobileEventsToShow(prev => prev + 4);
  }, []);

  const handleShowMoreCommunities = useCallback(() => {
    setMobileCommunitiesToShow(prev => prev + 4);
  }, []);

  // Filter upcoming events - only show active and upcoming events (not past events)
  const filteredUpcomingEvents = useMemo(() => {
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    
    return upcomingEvents.filter((event) => {
      const startTime = event.eventStartDateTime;
      const endTime = event.eventEndDateTime;

      // Show event if:
      // 1. Event is currently active (now is between start and end)
      // 2. Event is upcoming (start time is in the future)
      // Don't show if event has ended (end time is in the past)
      return (now >= startTime && now <= endTime) || (now < startTime);
    });
  }, [upcomingEvents]);

  // Calculate events to display
  const displayedUpcomingEvents = useMemo(() => {
    // On mobile, limit to mobileEventsToShow
    // On desktop, show all events (they'll be horizontally scrollable)
    if (isMobile) {
      return filteredUpcomingEvents.slice(0, mobileEventsToShow);
    }
    return filteredUpcomingEvents;
  }, [filteredUpcomingEvents, mobileEventsToShow, isMobile]);

  // Check if there are more events to show on mobile
  const hasMoreEvents = useMemo(() => {
    return isMobile && mobileEventsToShow < filteredUpcomingEvents.length;
  }, [filteredUpcomingEvents.length, mobileEventsToShow, isMobile]);

  // Calculate popular events to display (scrollable layout, show all events)
  const displayedPopularEvents = useMemo(() => {
    // Filter out unlisted events, then show all - they'll be horizontally scrollable
    return popularEvents.filter((event) => event.isPublic !== "3");
  }, [popularEvents]);

  const displayedCertificateEvents = useMemo(() => {
    return certificateEvents.filter((event) => event.isPublic !== "3");
  }, [certificateEvents]);

  // Calculate popular communities to display
  const displayedPopularCommunities = useMemo(() => {
    if (isMobile) {
      return popularChannels.slice(0, mobileCommunitiesToShow);
    }
    return popularChannels;
  }, [popularChannels, mobileCommunitiesToShow, isMobile]);

  // Check if there are more communities to show on mobile
  const hasMoreCommunities = useMemo(() => {
    return isMobile && mobileCommunitiesToShow < popularChannels.length;
  }, [popularChannels.length, mobileCommunitiesToShow, isMobile]);

  // Update scroll states when content changes or on scroll
  useEffect(() => {
    if (isMobile) return;
    
    const updateScrollStates = () => {
      checkScrollState(upcomingEventsScrollRef.current, setUpcomingEventsScrollState);
      checkScrollState(savedEventsScrollRef.current, setSavedEventsScrollState);
      checkScrollState(popularCommunitiesScrollRef.current, setPopularCommunitiesScrollState);
      checkScrollState(popularEventsScrollRef.current, setPopularEventsScrollState);
      checkScrollState(certificateEventsScrollRef.current, setCertificateEventsScrollState);
    };
    
    // Initial check with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(updateScrollStates, 100);
    
    // Check on scroll
    const upcomingContainer = upcomingEventsScrollRef.current;
    const savedContainer = savedEventsScrollRef.current;
    const communitiesContainer = popularCommunitiesScrollRef.current;
    const popularEventsContainer = popularEventsScrollRef.current;
    const certificateEventsContainer = certificateEventsScrollRef.current;
    
    const handleScroll = () => updateScrollStates();
    
    upcomingContainer?.addEventListener('scroll', handleScroll);
    savedContainer?.addEventListener('scroll', handleScroll);
    communitiesContainer?.addEventListener('scroll', handleScroll);
    popularEventsContainer?.addEventListener('scroll', handleScroll);
    certificateEventsContainer?.addEventListener('scroll', handleScroll);
    
    // Check on resize
    window.addEventListener('resize', updateScrollStates);
    
    return () => {
      clearTimeout(timeoutId);
      upcomingContainer?.removeEventListener('scroll', handleScroll);
      savedContainer?.removeEventListener('scroll', handleScroll);
      communitiesContainer?.removeEventListener('scroll', handleScroll);
      popularEventsContainer?.removeEventListener('scroll', handleScroll);
      certificateEventsContainer?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateScrollStates);
    };
  }, [isMobile, displayedUpcomingEvents, savedEvents, displayedPopularCommunities, displayedPopularEvents, displayedCertificateEvents, checkScrollState]);

  // Handle bookmark toggle
  const handleBookmarkToggle = useCallback(async (event: EventListItem) => {
    await toggleBookmark(event);
    // Refresh saved events if we're on the saved tab
    if (activeTab === 'saved') {
      await fetchSavedEvents();
    }
  }, [toggleBookmark, activeTab, fetchSavedEvents]);

  const handleEventClick = (event: EventListItem) => {
    navigate(generateEventUrl(event.eventTitle || event.title, event.eventID));
  };

  const handleCommunityClick = (community: ChannelListItem) => {
    const communityTitle = community.channelTitle || community.title;
    navigate(generateCommunityUrl(communityTitle, community.channelID));
  };

  const handleJoinCommunity = async (channelID: string) => {
    if (!token || !userId) {
      toastError(getTranslation('communities.joinError'));
      return;
    }

    try {
      const response = await Services.SocialHiveJoinToChannel(token, userId, channelID);
      if (response.data.status) {
        setLocalJoinedIds(prev => { const next = new Set(prev); next.add(channelID); return next; });
        toastSuccess(getTranslation('communities.joinSuccess'));
        refreshJoinedChannels();
        refreshPopularChannels();
      } else {
        toastError(response.data.message || getTranslation('communities.joinError'));
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('communities.joinError'));
    }
  };

  const handleLeaveCommunity = async (channelID: string) => {
    if (!token || !userId) {
      toastError(getTranslation('communities.leaveError'));
      return;
    }

    try {
      const response = await Services.SocialHiveLeaveChannel(token, userId, channelID);
      if (response.data.status) {
        setLocalJoinedIds(prev => {
          const next = new Set(prev);
          next.delete(channelID);
          return next;
        });
        toastSuccess(getTranslation('communities.leaveSuccess'));
        refreshJoinedChannels();
        refreshPopularChannels();
      } else {
        toastError(response.data.message || getTranslation('communities.leaveError'));
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('communities.leaveError'));
    }
  };

  return (
    <>
      <SEO
        title="Discover Events & Communities"
        description={getTranslation('footer.description')}
        url={typeof window !== 'undefined' ? `${window.location.origin}/` : undefined}
        structuredData={
          typeof window !== 'undefined'
            ? generateWebsiteSchema({
                siteName: 'ToGather',
                siteUrl: window.location.origin,
                description: getTranslation('footer.description'),
                searchUrlTemplate: `${window.location.origin}/events?search={search_term}`,
              })
            : undefined
        }
        lang={language.language}
      />
      <div className={styles.homePage}>
        <HeroSection />

      {/* Upcoming Events / Saved Events Section */}
      <section className={styles.section}>
        <div className={styles.upcomingEventsContainer}>
          <div className={styles.sectionHeaderWrapper}>
            <button
              className={`${styles.tabButton} ${activeTab === 'upcoming' ? styles.tabButtonActive : styles.tabButtonInactive}`}
              onClick={() => setActiveTab('upcoming')}
              type="button"
            >
              {getTranslation('home.upcomingEvents')}
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'saved' ? styles.tabButtonActive : styles.tabButtonInactive}`}
              onClick={() => setActiveTab('saved')}
              type="button"
            >
              {getTranslation('home.savedEvents')}
            </button>
          </div>
          
          {/* Upcoming Events Content */}
          <div className={`${styles.tabContent} ${activeTab === 'upcoming' ? styles.active : styles.hidden}`}>
            {loadingUpcoming ? (
              <div className={styles.eventRow}>
                {[1, 2, 3, 4].map((i) => (
                  <LoadingSkeleton key={i} width="100%" height={300} />
                ))}
              </div>
            ) : filteredUpcomingEvents.length > 0 ? (
              <div className={styles.scrollableContainer}>
                {!isMobile && (
                  <>
                    <div className={`${styles.scrollGradientLeft} ${upcomingEventsScrollState.canScrollLeft ? styles.visible : ''}`} />
                    <div className={`${styles.scrollGradientRight} ${upcomingEventsScrollState.canScrollRight ? styles.visible : ''}`} />
                    {upcomingEventsScrollState.canScrollLeft && (
                      <button
                        className={`${styles.chevronButton} ${styles.chevronLeft}`}
                        onClick={() => scrollByItems(upcomingEventsScrollRef.current, 'left')}
                        aria-label="Scroll left"
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        <HiChevronLeft />
                      </button>
                    )}
                    {upcomingEventsScrollState.canScrollRight && (
                      <button
                        className={`${styles.chevronButton} ${styles.chevronRight}`}
                        onClick={() => scrollByItems(upcomingEventsScrollRef.current, 'right')}
                        aria-label="Scroll right"
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        <HiChevronRight />
                      </button>
                    )}
                  </>
                )}
                <div 
                  className={styles.eventRow}
                  ref={upcomingEventsScrollRef}
                >
                  {displayedUpcomingEvents.map((event, index) => (
                    <div key={event.eventID} className={transitionStyles.staggerItem} style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}>
                      <PopularEventCard
                        event={event}
                        onClick={handleEventClick}
                      />
                    </div>
                  ))}
                </div>
                {hasMoreEvents && (
                  <div className={styles.showMoreContainer}>
                    <Button
                      variant="secondary"
                      size="medium"
                      onClick={handleShowMore}
                      className={styles.showMoreButton}
                    >
                      {getTranslation('events.showMore') || 'Show More'}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState title={getTranslation('home.noUpcomingEvents')} />
            )}
          </div>

          {/* Saved Events Content */}
          <div className={`${styles.tabContent} ${activeTab === 'saved' ? styles.active : styles.hidden}`}>
            {savedLoading ? (
              <div className={styles.eventRow}>
                {[1, 2, 3, 4].map((i) => (
                  <LoadingSkeleton key={i} width="100%" height={300} />
                ))}
              </div>
            ) : savedEvents.length > 0 ? (
              <div className={styles.scrollableContainer}>
                {!isMobile && (
                  <>
                    <div className={`${styles.scrollGradientLeft} ${savedEventsScrollState.canScrollLeft ? styles.visible : ''}`} />
                    <div className={`${styles.scrollGradientRight} ${savedEventsScrollState.canScrollRight ? styles.visible : ''}`} />
                    {savedEventsScrollState.canScrollLeft && (
                      <button
                        className={`${styles.chevronButton} ${styles.chevronLeft}`}
                        onClick={() => scrollByItems(savedEventsScrollRef.current, 'left')}
                        aria-label="Scroll left"
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        <HiChevronLeft />
                      </button>
                    )}
                    {savedEventsScrollState.canScrollRight && (
                      <button
                        className={`${styles.chevronButton} ${styles.chevronRight}`}
                        onClick={() => scrollByItems(savedEventsScrollRef.current, 'right')}
                        aria-label="Scroll right"
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        <HiChevronRight />
                      </button>
                    )}
                  </>
                )}
                <div 
                  className={styles.eventRow}
                  ref={savedEventsScrollRef}
                >
                  {savedEvents.filter((e) => e.isPublic !== "3").map((event) => (
                    <EventCard
                      key={event.eventID}
                      event={event}
                      onClick={handleEventClick}
                      showParticipantCount={false}
                      onBookmarkToggle={handleBookmarkToggle}
                      isSaved={isEventSaved(event.eventID)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState title={getTranslation('home.noSavedEvents')} />
            )}
          </div>
        </div>
      </section>

      {/* Popular Communities Section */}
      <section className={styles.section}>
        <div className={styles.popularCommunitiesContainer}>
          <div className={styles.sectionHeaderWrapperWithButton}>
            <button
              className={`${styles.tabButton} ${styles.tabButtonActive}`}
              type="button"
              disabled
            >
              {getTranslation('home.popularCommunities')}
            </button>
            <button
              className={styles.viewAllButton}
              onClick={() => navigate('/communities')}
              type="button"
            >
              {getTranslation('common.viewAll')}
            </button>
          </div>
          {loadingChannels ? (
            <div className={styles.communityRow}>
              {[1, 2, 3, 4].map((i) => (
                <LoadingSkeleton key={i} width="100%" height={200} />
              ))}
            </div>
          ) : popularChannels.length > 0 ? (
            <div className={styles.scrollableContainer}>
              {!isMobile && (
                <>
                  <div className={`${styles.scrollGradientLeft} ${popularCommunitiesScrollState.canScrollLeft ? styles.visible : ''}`} />
                  <div className={`${styles.scrollGradientRight} ${popularCommunitiesScrollState.canScrollRight ? styles.visible : ''}`} />
                  {popularCommunitiesScrollState.canScrollLeft && (
                    <button
                      className={`${styles.chevronButton} ${styles.chevronLeft}`}
                      onClick={() => scrollByItems(popularCommunitiesScrollRef.current, 'left')}
                      aria-label="Scroll left"
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiChevronLeft />
                    </button>
                  )}
                  {popularCommunitiesScrollState.canScrollRight && (
                    <button
                      className={`${styles.chevronButton} ${styles.chevronRight}`}
                      onClick={() => scrollByItems(popularCommunitiesScrollRef.current, 'right')}
                      aria-label="Scroll right"
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiChevronRight />
                    </button>
                  )}
                </>
              )}
              <div 
                className={styles.communityRow}
                ref={popularCommunitiesScrollRef}
              >
                {displayedPopularCommunities.map((channel, index) => {
                  const channelID = channel.channelID;
                  const isAdmin = channelAdminMap.get(channelID) || false;
                  // If user is admin, override the community's isAdmin field
                  const communityWithAdmin = isAdmin
                    ? { ...channel, isAdmin: "1" as const }
                    : channel;
                  return (
                    <div key={channelID} className={transitionStyles.staggerItem} style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}>
                      <CommunityCard
                        community={communityWithAdmin as ChannelListItem}
                        isJoined={joinedChannelIds.has(channelID)}
                        onJoin={handleJoinCommunity}
                        onLeave={handleLeaveCommunity}
                        onClick={handleCommunityClick}
                        disableHoverLift
                      />
                    </div>
                  );
                })}
              </div>
              {hasMoreCommunities && (
                <div className={styles.showMoreContainer}>
                  <Button
                    variant="secondary"
                    size="medium"
                    onClick={handleShowMoreCommunities}
                    className={styles.showMoreButton}
                  >
                    {getTranslation('events.showMore') || 'Show More'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <EmptyState title={getTranslation('home.noPopularCommunities')} />
          )}
        </div>
      </section>

      {/* Popular Events Section */}
      <section className={styles.section}>
        <div className={styles.popularEventsContainer}>
          <div className={styles.sectionHeaderWrapperWithButton}>
            <button
              className={`${styles.tabButton} ${styles.tabButtonActive}`}
              type="button"
              disabled
            >
              {getTranslation('home.popularEvents')}
            </button>
            <button
              className={styles.viewAllButton}
              onClick={() => navigate('/events')}
              type="button"
            >
              {getTranslation('common.viewAll')}
            </button>
          </div>
          {loadingPopular ? (
            <div className={styles.eventRow}>
              {[1, 2, 3, 4].map((i) => (
                <LoadingSkeleton key={i} width="100%" height={300} />
              ))}
            </div>
          ) : popularEvents.length > 0 ? (
            <div className={styles.scrollableContainer}>
              {!isMobile && (
                <>
                  <div className={`${styles.scrollGradientLeft} ${popularEventsScrollState.canScrollLeft ? styles.visible : ''}`} />
                  <div className={`${styles.scrollGradientRight} ${popularEventsScrollState.canScrollRight ? styles.visible : ''}`} />
                  {popularEventsScrollState.canScrollLeft && (
                    <button
                      className={`${styles.chevronButton} ${styles.chevronLeft}`}
                      onClick={() => scrollByItems(popularEventsScrollRef.current, 'left')}
                      aria-label="Scroll left"
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiChevronLeft />
                    </button>
                  )}
                  {popularEventsScrollState.canScrollRight && (
                    <button
                      className={`${styles.chevronButton} ${styles.chevronRight}`}
                      onClick={() => scrollByItems(popularEventsScrollRef.current, 'right')}
                      aria-label="Scroll right"
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiChevronRight />
                    </button>
                  )}
                </>
              )}
              <div 
                className={styles.popularEventsRow}
                ref={popularEventsScrollRef}
              >
                {displayedPopularEvents.map((event) => (
                  <PopularEventCard
                    key={event.eventID}
                    event={event}
                    onClick={handleEventClick}
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyState title={getTranslation('home.noPopularEvents')} />
          )}
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.popularEventsContainer}>
          <div className={styles.sectionHeaderWrapperWithButton}>
            <button
              className={`${styles.tabButton} ${styles.tabButtonActive}`}
              type="button"
              disabled
            >
              {getTranslation('home.certificateEvents')}
            </button>
            <button
              className={styles.viewAllButton}
              onClick={() => navigate('/events')}
              type="button"
            >
              {getTranslation('common.viewAll')}
            </button>
          </div>
          {loadingCertificateEvents ? (
            <div className={styles.eventRow}>
              {[1, 2, 3, 4].map((i) => (
                <LoadingSkeleton key={i} width="100%" height={300} />
              ))}
            </div>
          ) : displayedCertificateEvents.length > 0 ? (
            <div className={styles.scrollableContainer}>
              {!isMobile && (
                <>
                  <div className={`${styles.scrollGradientLeft} ${certificateEventsScrollState.canScrollLeft ? styles.visible : ''}`} />
                  <div className={`${styles.scrollGradientRight} ${certificateEventsScrollState.canScrollRight ? styles.visible : ''}`} />
                  {certificateEventsScrollState.canScrollLeft && (
                    <button
                      className={`${styles.chevronButton} ${styles.chevronLeft}`}
                      onClick={() => scrollByItems(certificateEventsScrollRef.current, 'left')}
                      aria-label="Scroll left"
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiChevronLeft />
                    </button>
                  )}
                  {certificateEventsScrollState.canScrollRight && (
                    <button
                      className={`${styles.chevronButton} ${styles.chevronRight}`}
                      onClick={() => scrollByItems(certificateEventsScrollRef.current, 'right')}
                      aria-label="Scroll right"
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiChevronRight />
                    </button>
                  )}
                </>
              )}
              <div
                className={styles.popularEventsRow}
                ref={certificateEventsScrollRef}
              >
                {displayedCertificateEvents.map((event) => (
                  <PopularEventCard
                    key={event.eventID}
                    event={event}
                    onClick={handleEventClick}
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyState title={getTranslation('home.noCertificateEvents')} />
          )}
        </div>
      </section>
      </div>
    </>
  );
};

export default HomePage;
